import { css, CSSObject, SerializedStyles } from '@emotion/react';
import {
    colorToCssValue,
    ColorValue,
    createResponsiveStyles,
    getSizeValue,
    mergeResponsiveStyleValues,
    ResponsiveStyleValue,
    SpacingValue,
    useHoneycombTheme,
} from '../../theming';

// Static styles
export const STATIC_STYLES: SerializedStyles = css`
    --scroller-nav-button-size: 24px;
    --scroller-nav-button-margin: 4px;
    --scroller-nav-button-icon-size: 12px;

    position: relative;

    margin-inline-start: var(--scroller-margin-x, 0);
    margin-inline-end: var(--scroller-margin-x, 0);

    [data-scroller-track] {
        display: flex;
        flex-wrap: var(--scroller-flex-wrap);
        column-gap: var(--scroller-column-gap);
        row-gap: var(--scroller-row-gap);

        overflow-x: scroll;
        -webkit-overflow-scrolling: touch;
        &::-webkit-scrollbar {
            display: none;
        }
        scrollbar-width: none;

        padding-inline-start: var(--scroller-track-padding-x, 0);
        padding-inline-end: var(--scroller-track-padding-x, 0);
    }

    [data-scroller-item] {
        flex-shrink: var(--scroller-item-shrink);
    }

    [data-scroller-nav] {
        pointer-events: none;
        position: absolute;
        z-index: 1;
        top: 0;
        bottom: 0;
        justify-content: flex-start;

        display: flex;
        align-items: center;
        justify-content: flex-start;

        visibility: hidden;
        opacity: 0;

        transition:
            opacity 0.2s ease,
            visibility 0.2s ease,
            transform 0.2s ease;

        &:after {
            content: '';
            pointer-events: none;
            display: block;
            width: 40px;
            height: 100%;
        }

        &[data-scroller-nav='start'] {
            inset-inline-start: 0;
            inset-inline-end: auto;
            flex-direction: row;
            padding-inline-start: var(--scroller-track-padding-x, 0);

            &[data-with-gradient] {
                background-image: linear-gradient(to left, rgba(255, 255, 255, 0), var(--scroller-bg-color) 40px);
                &:dir(rtl) {
                    background-image: linear-gradient(to right, rgba(255, 255, 255, 0), var(--scroller-bg-color) 40px);
                }
            }
        }

        &[data-scroller-nav='end'] {
            inset-inline-start: auto;
            inset-inline-end: 0;
            flex-direction: row-reverse;
            padding-inline-end: var(--scroller-track-padding-x, 0);

            &[data-with-gradient] {
                background-image: linear-gradient(to right, rgba(255, 255, 255, 0), var(--scroller-bg-color) 40px);
                &:dir(rtl) {
                    background-image: linear-gradient(to left, rgba(255, 255, 255, 0), var(--scroller-bg-color) 40px);
                }
            }
        }

        > button {
            pointer-events: all;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: var(--scroller-nav-button-size);
            height: var(--scroller-nav-button-size);
            border-radius: 50%;

            background-color: #fff;
            color: var(--hc-color-text-body);
            border: solid 1px var(--hc-color-border);

            opacity: 0.9;
            transition:
                box-shadow 0.2s ease,
                opacity 0.2s ease;

            &:hover {
                box-shadow: var(--hc-shadow-s);
                opacity: 1;
            }

            > svg {
                width: var(--scroller-nav-button-icon-size);
                height: var(--scroller-nav-button-icon-size);
            }

            @media (pointer: coarse) {
                display: none;
            }
        }

        &[data-button-color='light'] {
            > button {
                background-color: #fff;
                color: var(--hc-color-text-body)
                border-color: var(--hc-color-border);
            }
        }
        &[data-button-color='dark'] {
            > button {
                background-color: var(--hc-color-grey-tint-700);
                color: #FFF;
                border-color: #FFF;
            }
        }

        &[data-show] {
            visibility: visible;
            opacity: 1;
        }
    }
`;

function getResponsiveVariables(value: {
    columnSpacing: SpacingValue;
    rowSpacing: SpacingValue;
    disableScrolling: boolean;
}): CSSObject {
    const { columnSpacing, rowSpacing, disableScrolling } = value;
    return {
        '--scroller-column-gap': `calc(var(--hc-spacing-unit) * ${columnSpacing})`,
        '--scroller-row-gap': `calc(var(--hc-spacing-unit) * ${rowSpacing})`,
        '--scroller-flex-wrap': disableScrolling ? 'wrap' : 'nowrap',
        '--scroller-item-shrink': disableScrolling ? '1' : '0',
    };
}

export function useDynamicStyles(
    columnSpacing?: ResponsiveStyleValue<SpacingValue>,
    rowSpacing?: ResponsiveStyleValue<SpacingValue>,
    disableScrolling?: ResponsiveStyleValue<boolean>,
    contained?: ResponsiveStyleValue<boolean>,
    overflowGradientColor?: ColorValue
): CSSObject {
    const { theme } = useHoneycombTheme();

    return {
        '--scroller-bg-color': colorToCssValue(overflowGradientColor ?? 'background.content'),
        ...createResponsiveStyles(
            [
                {
                    value: mergeResponsiveStyleValues({ columnSpacing, rowSpacing, disableScrolling }, theme),
                    getStyles: getResponsiveVariables,
                },
                ...(contained
                    ? [
                          {
                              value: theme.breakpoints.contentMargins,
                              getStyles: (value: number) => {
                                  const margin = getSizeValue(value * -1);
                                  const padding = getSizeValue(value);
                                  return {
                                      '--scroller-margin-x': margin,
                                      '--scroller-track-padding-x': padding,
                                  };
                              },
                          },
                      ]
                    : []),
            ],
            theme
        ),
    };
}
